import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Building2, Plus, Trash2, Pencil, Save, X, Star, AlertTriangle, RefreshCw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useBulkSelect, BulkSelectAll, BulkSelectCheckbox, BulkDeleteBar } from "@/components/BulkDeleteBar";

const EMPTY = {
  name: "", legal_name: "", uid: "", tax_number: "",
  address: "", city: "", zip_code: "", country: "Österreich",
  iban: "", bic: "", bank_name: "",
  phone: "", email: "", website: "",
  logo_url: "", signature_html: "", payroll_footer: "",
  color: "#3b82f6", active: true, sort_order: 100, is_default: false,
  // Phase G+ Phase 2 (28.05.2026) — Company profile fields for Onboarding + Mindestanmeldung
  firmennummer: "", beitragskontonummer: "", krankenkasse: "NÖ Krankenkasse", operation: "",
};

export default function FirmsAdmin() {
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // firm being edited (or "new")
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  // Bulk-delete excludes the default firm (backend already rejects, but we
  // shouldn't let the user even select it).
  const selectableFirms = firms.filter(f => !f.is_default);
  const sel = useBulkSelect(selectableFirms);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/firms/all");
      setFirms(data || []);
    } catch (e) {
      toast.error("Firmen konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  function startNew() { setEditing("new"); setForm({ ...EMPTY }); }
  function startEdit(f) { setEditing(f.id); setForm({ ...EMPTY, ...f }); }
  function cancel() { setEditing(null); setForm(EMPTY); }
  function patch(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function save() {
    if (!form.name?.trim()) { toast.error("Firmenname erforderlich"); return; }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/firms", form);
        toast.success(`Firma „${form.name}" angelegt`);
      } else {
        await api.put(`/firms/${editing}`, form);
        toast.success(`Firma „${form.name}" aktualisiert`);
      }
      cancel();
      await load();
    } catch (e) {
      toast.error(`Speichern fehlgeschlagen: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function remove(f) {
    if (!window.confirm(`Firma „${f.name}" wirklich endgültig löschen?\n\nDieser Vorgang kann nicht rückgängig gemacht werden. Die Firma wird vollständig entfernt — Mitarbeiter, Abteilungen und Fahrzeuge müssen vorher umgebucht sein.`)) return;
    try {
      await api.delete(`/firms/${f.id}`);
      toast.success("Firma gelöscht");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  }

  async function makeDefault(f) {
    try {
      await api.post(`/firms/${f.id}/make-default`);
      toast.success(`„${f.name}" ist jetzt die Standard-Firma`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehlgeschlagen");
    }
  }

  async function runMigration() {
    try {
      const { data } = await api.post("/firms/migrate");
      const u = data.updated || {};
      toast.success(`Migration: ${u.departments || 0} Abteilungen, ${u.employees || 0} Mitarbeiter, ${u.vehicles || 0} Fahrzeuge aktualisiert`);
      await load();
    } catch (e) {
      toast.error("Migration fehlgeschlagen");
    }
  }

  if (loading) return <div className="text-sm text-zinc-500">Lade Firmen…</div>;

  return (
    <div className="space-y-6" data-testid="firms-admin">
      <div className="flex items-start justify-between gap-4 pb-3 border-b border-zinc-800">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-zinc-100">Firmen (Schwestergesellschaften)</h3>
            <p className="text-xs text-zinc-500 mt-0.5 max-w-2xl">
              Jede Abteilung gehört zu einer Firma. Mitarbeiter erben die Firma aus
              ihrer Abteilung — ein Fahrer kann jedoch für eine andere Firma als
              seine Heimat-Abteilung tätig sein (Aushilfe-Modus).
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={runMigration}
            data-testid="firms-migrate-btn"
            className="inline-flex items-center gap-2 px-3 py-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-300 rounded text-xs transition-colors"
            title="Alle Abteilungen/Mitarbeiter/Fahrzeuge ohne Firma zur Standard-Firma zuordnen"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Migration prüfen
          </button>
          <button
            onClick={startNew}
            data-testid="firms-add-btn"
            disabled={editing !== null}
            className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-900 text-white rounded text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" /> Neue Firma
          </button>
        </div>
      </div>

      {/* Editor */}
      {editing !== null && (
        <div className="border border-blue-500/30 bg-blue-500/5 rounded-md p-5 space-y-4" data-testid="firm-editor">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-blue-300">
              {editing === "new" ? "Neue Firma anlegen" : `Firma bearbeiten: ${form.name}`}
            </h4>
            <div className="flex gap-2">
              <button onClick={save} disabled={saving} data-testid="firm-save"
                className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-sm font-medium">
                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Speichern
              </button>
              <button onClick={cancel} data-testid="firm-cancel"
                className="inline-flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded text-sm">
                <X className="w-4 h-4" /> Abbrechen
              </button>
            </div>
          </div>

          <Section title="Allgemein">
            <F label="Firmenname (Kurz)" required>
              <In value={form.name} onChange={(v) => patch("name", v)} testid="firm-name" />
            </F>
            <F label="Vollständiger Name (Rechtsform)">
              <In value={form.legal_name} onChange={(v) => patch("legal_name", v)} testid="firm-legal-name"
                placeholder="z.B. Munja A-Transport GmbH" />
            </F>
            <F label="UID-Nummer (USt-IdNr)">
              <In value={form.uid} onChange={(v) => patch("uid", v)} testid="firm-uid"
                placeholder="ATU12345678" />
            </F>
            <F label="Steuernummer">
              <In value={form.tax_number} onChange={(v) => patch("tax_number", v)} testid="firm-tax" />
            </F>
            <F label="Farbe (UI-Akzent)">
              <input type="color" value={form.color || "#3b82f6"} onChange={(e) => patch("color", e.target.value)}
                data-testid="firm-color"
                className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded cursor-pointer" />
            </F>
            <F label="Sortierreihenfolge">
              <In type="number" value={form.sort_order} onChange={(v) => patch("sort_order", parseInt(v || "0", 10))}
                testid="firm-sort" />
            </F>
          </Section>

          {/* Phase G+ Phase 2 (28.05.2026) — Lohn-/Mindestanmeldungsdaten */}
          <Section title="Lohn & Mindestanmeldung">
            <F label="Firmennummer (intern)">
              <In value={form.firmennummer} onChange={(v) => patch("firmennummer", v)} testid="firm-firmennummer" />
            </F>
            <F label="Beitragskontonummer / Kostenstelle">
              <In value={form.beitragskontonummer} onChange={(v) => patch("beitragskontonummer", v)} testid="firm-beitragskontonummer"
                placeholder="z.B. 12345678" />
            </F>
            <F label="Krankenkasse">
              <In value={form.krankenkasse} onChange={(v) => patch("krankenkasse", v)} testid="firm-krankenkasse"
                placeholder="NÖ Krankenkasse" />
            </F>
            <F label="Operation / Bereich">
              <select value={form.operation || ""} onChange={(e) => patch("operation", e.target.value)}
                data-testid="firm-operation"
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm">
                <option value="">— wählen —</option>
                <option value="DPD">DPD</option>
                <option value="UPS">UPS</option>
                <option value="GLS">GLS</option>
                <option value="Amazon">Amazon</option>
                <option value="DHL">DHL</option>
                <option value="Sonstige">Sonstige</option>
              </select>
            </F>
          </Section>

          <Section title="Adresse">
            <F label="Straße + Nr">
              <In value={form.address} onChange={(v) => patch("address", v)} testid="firm-addr" />
            </F>
            <F label="PLZ">
              <In value={form.zip_code} onChange={(v) => patch("zip_code", v)} testid="firm-zip" />
            </F>
            <F label="Ort">
              <In value={form.city} onChange={(v) => patch("city", v)} testid="firm-city" />
            </F>
            <F label="Land">
              <In value={form.country} onChange={(v) => patch("country", v)} testid="firm-country" />
            </F>
          </Section>

          <Section title="Bankverbindung">
            <F label="IBAN">
              <In value={form.iban} onChange={(v) => patch("iban", v)} testid="firm-iban"
                placeholder="AT00 0000 0000 0000 0000" />
            </F>
            <F label="BIC / SWIFT">
              <In value={form.bic} onChange={(v) => patch("bic", v)} testid="firm-bic" />
            </F>
            <F label="Bankname">
              <In value={form.bank_name} onChange={(v) => patch("bank_name", v)} testid="firm-bank" />
            </F>
          </Section>

          <Section title="Kontakt & Briefkopf">
            <F label="Telefon">
              <In value={form.phone} onChange={(v) => patch("phone", v)} testid="firm-phone" />
            </F>
            <F label="E-Mail">
              <In type="email" value={form.email} onChange={(v) => patch("email", v)} testid="firm-email" />
            </F>
            <F label="Website">
              <In value={form.website} onChange={(v) => patch("website", v)} testid="firm-website" />
            </F>
            <F label="Logo" full>
              <LogoField firmId={editing} logoUrl={form.logo_url} onChange={(url) => patch("logo_url", url)} />
            </F>
          </Section>

          <Section title="PDF & E-Mail Footer">
            <F label="E-Mail-Signatur (HTML)" full>
              <textarea value={form.signature_html} onChange={(e) => patch("signature_html", e.target.value)}
                data-testid="firm-signature" rows={3}
                placeholder="<p>Mit freundlichen Grüßen<br/>Munja A-Transport GmbH</p>"
                className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 font-mono focus:border-blue-500 outline-none" />
            </F>
            <F label="Lohnzettel-/Rechnungs-Fußzeile (HTML)" full>
              <textarea value={form.payroll_footer} onChange={(e) => patch("payroll_footer", e.target.value)}
                data-testid="firm-footer" rows={3}
                placeholder="<small>Firmenbuchnr ▪ FN xxxxxx ▪ Gerichtsstand</small>"
                className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 font-mono focus:border-blue-500 outline-none" />
            </F>
          </Section>

          <div className="flex items-center gap-4 pt-2 border-t border-zinc-800">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.active}
                onChange={(e) => patch("active", e.target.checked)} data-testid="firm-active"
                className="w-4 h-4 accent-emerald-500" />
              <span className="text-sm text-zinc-200">Aktiv</span>
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.is_default}
                onChange={(e) => patch("is_default", e.target.checked)} data-testid="firm-default"
                className="w-4 h-4 accent-amber-500" />
              <span className="text-sm text-zinc-200">Als Standard-Firma setzen</span>
            </label>
          </div>
        </div>
      )}

      {/* List */}
      {firms.length > 1 && (
        <div className="flex items-center gap-2 px-3 py-2 mb-2 bg-zinc-950/40 border border-zinc-800/50 rounded text-xs text-zinc-500">
          <BulkSelectAll sel={sel} testid="firm-select-all" />
          <span>Alle löschbaren Firmen auswählen (Standard-Firma ausgenommen)</span>
        </div>
      )}
      <div className="space-y-2">
        {firms.length === 0 ? (
          <div className="text-sm text-zinc-500 px-3 py-4 bg-zinc-950/50 border border-zinc-800/50 rounded">
            Noch keine Firmen angelegt.
          </div>
        ) : (
          firms.map(f => (
            <div key={f.id}
              className={`flex items-center gap-3 p-3 border rounded transition-colors ${
                f.active
                  ? "bg-zinc-950/60 border-zinc-800 hover:border-zinc-700"
                  : "bg-zinc-950/20 border-zinc-900 opacity-60"
              }`}
              data-testid={`firm-row-${f.id}`}
            >
              <div className="w-5 flex-shrink-0">
                {!f.is_default && (
                  <BulkSelectCheckbox sel={sel} id={f.id} testid={`firm-select-${f.id}`} />
                )}
              </div>
              <div className="w-3 h-10 rounded-sm flex-shrink-0" style={{ background: f.color || "#3b82f6" }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-zinc-100 truncate">{f.name}</span>
                  {f.is_default && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-[10px] font-semibold text-amber-300 uppercase tracking-wider" data-testid={`firm-default-badge-${f.id}`}>
                      <Star className="w-2.5 h-2.5 fill-current" /> Standard
                    </span>
                  )}
                  {!f.active && (
                    <span className="px-1.5 py-0.5 rounded bg-zinc-700/40 text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">Deaktiviert</span>
                  )}
                </div>
                <div className="text-xs text-zinc-500 truncate">
                  {f.legal_name || f.name}
                  {f.uid && ` · UID: ${f.uid}`}
                  {f.city && ` · ${f.city}`}
                </div>
              </div>
              <div className="flex gap-1">
                {!f.is_default && f.active && (
                  <button onClick={() => makeDefault(f)} title="Als Standard setzen"
                    data-testid={`firm-make-default-${f.id}`}
                    className="p-1.5 text-amber-400 hover:bg-amber-500/10 rounded transition-colors">
                    <Star className="w-4 h-4" />
                  </button>
                )}
                <button onClick={() => startEdit(f)} disabled={editing !== null} title="Bearbeiten"
                  data-testid={`firm-edit-${f.id}`}
                  className="p-1.5 text-blue-400 hover:bg-blue-500/10 rounded transition-colors disabled:opacity-30">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => remove(f)} disabled={f.is_default} title="Löschen"
                  data-testid={`firm-delete-${f.id}`}
                  className="p-1.5 text-red-400 hover:bg-red-500/10 rounded transition-colors disabled:opacity-30">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="mt-4 px-3 py-2.5 bg-blue-500/5 border border-blue-500/20 rounded text-xs text-zinc-400 flex items-start gap-2">
        <CheckCircle2 className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
        <div>
          Sobald eine neue Firma angelegt ist, können Sie sie in <em>Einstellungen → Abteilungen</em>
          zuweisen. Mitarbeiter, Fahrzeuge und alle Dokumente erben dann automatisch die richtige
          Firma. Im nächsten Update werden auch PDF-Briefköpfe je Firma getrennt.
        </div>
      </div>
      <BulkDeleteBar
        sel={sel}
        label="Firmen"
        deleteEndpoint={(id) => `/firms/${id}`}
        onAfter={load}
        testid="firm-bulk-delete"
      />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2 pb-1.5 border-b border-zinc-800">
        {title}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {children}
      </div>
    </div>
  );
}

function F({ label, required, full, children }) {
  return (
    <div className={full ? "md:col-span-2 lg:col-span-3" : ""}>
      <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-1 mb-1">
        <span>{label}</span>
        {required && <span className="text-red-400">*</span>}
      </label>
      {children}
    </div>
  );
}

function In({ type = "text", value, onChange, placeholder, testid }) {
  return (
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:border-blue-500 outline-none"
    />
  );
}

function LogoField({ firmId, logoUrl, onChange }) {
  const [uploading, setUploading] = useState(false);
  // Construct full preview URL — relative path is relative to /api
  function previewUrl() {
    if (!logoUrl) return null;
    if (logoUrl.startsWith("http")) return logoUrl;
    if (logoUrl.startsWith("/api/")) {
      // Resolve via backend base URL
      const base = process.env.REACT_APP_BACKEND_URL || "";
      return `${base}${logoUrl}?t=${Date.now()}`;  // cache-bust on refresh
    }
    return logoUrl;
  }

  async function upload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!firmId || firmId === "new") {
      toast.error("Bitte erst die Firma speichern, dann Logo hochladen");
      e.target.value = "";
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Max. 2 MB");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/firms/${firmId}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.logo_url || "");
      toast.success("Logo hochgeladen");
    } catch (err) {
      toast.error(`Upload fehlgeschlagen: ${err.response?.data?.detail || err.message}`);
    } finally {
      setUploading(false);
      const input = e.target;
      if (input) input.value = "";
    }
  }

  async function removeLogo() {
    if (!firmId || firmId === "new") return;
    try {
      await api.delete(`/firms/${firmId}/logo`);
      onChange("");
      toast.success("Logo entfernt");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  }

  return (
    <div className="flex items-start gap-4">
      <div className="w-24 h-24 bg-zinc-950 border border-zinc-800 rounded flex items-center justify-center overflow-hidden flex-shrink-0" data-testid="firm-logo-preview">
        {previewUrl() ? (
          <img src={previewUrl()} alt="Firmenlogo" className="max-w-full max-h-full object-contain" />
        ) : (
          <Building2 className="w-8 h-8 text-zinc-700" />
        )}
      </div>
      <div className="flex-1">
        <label className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded text-sm font-medium cursor-pointer transition-colors">
          {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          {uploading ? "Lade…" : "Logo hochladen"}
          <input
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            onChange={upload}
            disabled={uploading || firmId === "new" || !firmId}
            data-testid="firm-logo-upload"
            className="hidden"
          />
        </label>
        {logoUrl && firmId !== "new" && (
          <button
            type="button"
            onClick={removeLogo}
            data-testid="firm-logo-remove"
            className="ml-2 inline-flex items-center gap-1 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded text-sm transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" /> Entfernen
          </button>
        )}
        <p className="text-[11px] text-zinc-500 mt-2">
          PNG, JPG, SVG oder WebP. Max. 2 MB. Wird auf allen PDFs und im Firmen-Umschalter angezeigt.
          {firmId === "new" && <span className="block text-amber-400 mt-0.5">Bitte erst Firma speichern, dann Logo hochladen.</span>}
        </p>
      </div>
    </div>
  );
}
